import { FormEvent, useState } from "react";
import {
  useQuery,
  getUpkeepRequests,
  createUpkeepRequest,
  updateUpkeepRequestStatus,
} from "wasp/client/operations";
import { Plus, Sparkles } from "lucide-react";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../../client/hooks/useRequireSectionVisible";
import { useLocationMembership } from "../../../client/hooks/useLocationMembership";
import { MembersOnlyNotice } from "../../../client/components/MembersOnlyNotice";
import {
  UPKEEP_CATEGORY_LABELS,
  UPKEEP_STATUSES,
  UPKEEP_STATUS_LABELS,
  type UpkeepStatus,
} from "../modules";

const URGENCY_LABELS: Record<string, string> = {
  LOW: "Whenever possible",
  NORMAL: "Today",
  HIGH: "Urgent (safety risk)",
};

const STATUS_STYLES: Record<UpkeepStatus, string> = {
  OPEN: "bg-amber-100 text-amber-800",
  IN_PROGRESS: "bg-sky-100 text-sky-800",
  RESOLVED: "bg-emerald-100 text-emerald-800",
  CLOSED: "bg-neutral-100 text-neutral-600",
};

export default function UpkeepRequestsPage() {
  useRequireSectionVisible("showUpkeepRequest");
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id || "";
  const { isMember, isLoading: memberLoading, locationCode } =
    useLocationMembership();

  const [statusFilter, setStatusFilter] = useState<UpkeepStatus | "">("");
  const { data, isLoading, refetch } = useQuery(
    getUpkeepRequests,
    { locationId, status: statusFilter || undefined },
    { enabled: !!locationId },
  );
  const isManager = !!data?.isManager;
  const list = (data?.requests as any[]) || [];

  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState("SPILL");
  const [area, setArea] = useState("");
  const [urgency, setUrgency] = useState("NORMAL");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setBusy(true);
    setError("");
    try {
      await createUpkeepRequest({
        locationId,
        category,
        area,
        urgency,
        description,
      });
      setArea("");
      setDescription("");
      setUrgency("NORMAL");
      setShowForm(false);
      await refetch();
    } catch (err: any) {
      setError(err?.message || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  async function onStatus(id: string, status: UpkeepStatus) {
    try {
      await updateUpkeepRequestStatus({ id, status });
      await refetch();
    } catch (err: any) {
      alert(err?.message || "Could not update status");
    }
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
              Members
            </p>
            <h1 className="mt-1 text-3xl font-bold">Cleaning &amp; Upkeep</h1>
            <p className="mt-2 text-neutral-600">
              {isManager
                ? "Requests from members for this location. Update the status as your team handles them."
                : "Report a spill, a washroom that needs cleaning, overflowing trash, and similar issues."}
            </p>
          </div>
          {isMember && (
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white"
            >
              <Plus className="h-4 w-4" />
              New request
            </button>
          )}
        </div>

        {!locationId && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Select a location to view upkeep requests.
          </p>
        )}

        {locationId && !memberLoading && !isMember && (
          <div className="mb-6">
            <MembersOnlyNotice locationCode={locationCode} />
          </div>
        )}

        {showForm && isMember && (
          <form
            onSubmit={onCreate}
            className="mb-6 space-y-3 rounded-2xl border border-neutral-200 bg-white p-5"
          >
            <label className="block text-sm">
              <span className="mb-1 block font-medium">What's the problem?</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              >
                {Object.entries(UPKEEP_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Where?</span>
              <input
                required
                value={area}
                onChange={(e) => setArea(e.target.value)}
                maxLength={120}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                placeholder="e.g. 3rd floor men's washroom, lobby near elevators"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">How soon?</span>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              >
                {Object.entries(URGENCY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Details (optional)</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                rows={3}
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </form>
        )}

        {locationId && (
          <div className="mb-4 flex flex-wrap gap-2">
            {(["", ...UPKEEP_STATUSES] as const).map((s) => (
              <button
                key={s || "ALL"}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  statusFilter === s
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-neutral-300 text-neutral-600"
                }`}
              >
                {s ? UPKEEP_STATUS_LABELS[s] : "All"}
              </button>
            ))}
          </div>
        )}

        {isLoading && <p className="text-neutral-500">Loading…</p>}

        <div className="space-y-3">
          {list.map((row) => {
            const meta = (row.metadata as any) || {};
            const status: UpkeepStatus = meta.status || "OPEN";
            return (
              <article
                key={row.id}
                className="rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div className="flex gap-3">
                  <div className="rounded-xl bg-emerald-50 p-2 text-emerald-700">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h2 className="font-semibold">{row.title}</h2>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status]}`}
                      >
                        {UPKEEP_STATUS_LABELS[status]}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500">
                      {meta.urgency === "HIGH" && (
                        <span className="mr-1 font-semibold text-red-600">
                          Urgent ·
                        </span>
                      )}
                      {new Date(row.createdAt).toLocaleString()}
                      {isManager && row.author
                        ? ` · ${row.author.fullName || row.author.username || row.author.email}`
                        : ""}
                    </p>
                    {row.description && (
                      <p className="mt-1 text-sm text-neutral-600">
                        {row.description}
                      </p>
                    )}
                    {isManager && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {UPKEEP_STATUSES.map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={status === s}
                            className="rounded-lg border px-2 py-1 text-xs font-medium disabled:opacity-40"
                            onClick={() => onStatus(row.id, s)}
                          >
                            {UPKEEP_STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          {locationId && !isLoading && list.length === 0 && (
            <p className="rounded-2xl border border-dashed border-neutral-200 px-4 py-10 text-center text-neutral-500">
              {isManager
                ? "No upkeep requests yet."
                : "You haven't submitted any upkeep requests yet."}
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
